import { parseReferences } from '@/features/agentic-insights/components/data/types';

export interface EchoLookup {
  objective: (id: number) => string | undefined;
  item: (id: number) => { externalKey?: string; name?: string } | undefined;
}

/** Every spelling of a reference that counts as the same thing said again. */
const echoesOf = (kind: 'item' | 'objective', id: number, refs: EchoLookup): string[] => {
  if (kind === 'objective') {
    const title = refs.objective(id);
    return title ? [title] : [];
  }

  const item = refs.item(id);
  if (!item) return [];

  const joined = [item.externalKey, item.name].filter(Boolean).join(' · ');
  return [joined, item.name, item.externalKey].filter((value): value is string => Boolean(value));
};

const escapeForRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Strips the name a reference token is immediately followed by, where that name is what the token
 * already resolves to.
 *
 * A description block's lead is written as the thing it is about — `[objective:68] Increase Digital
 * Revenue`, `[item:118834] "test"` — so a resolved token prints the name and the sentence then prints
 * it again. Inline, in the classic tab's flowing paragraph, the repetition is only slightly odd. In a
 * column of names a reader scans down it is the whole column, twice, wrapped over three lines.
 *
 * Only an exact echo goes: matching is against the label the token itself would render (an item also
 * matches its bare name or key, since a lead quotes the name alone), and anything else the author
 * wrote after the reference is left exactly as it was.
 */
export const stripReferenceEcho = (text: string, refs: EchoLookup): string => {
  const tokens = parseReferences(text);
  if (tokens.length === 0) return text;

  let result = '';
  let cursor = 0;

  for (const token of tokens) {
    result += text.slice(cursor, token.index + token.raw.length);
    cursor = token.index + token.raw.length;

    const rest = text.slice(cursor);
    for (const echo of echoesOf(token.kind, token.id, refs)) {
      // Leads quote a name — `"test"` — so the quotes are part of the echo, not of the sentence.
      //
      // The lookahead is what keeps this to whole words. Without it the echo matches a PREFIX, so a
      // label of `test` would strip the `test` out of an author's `testy` and leave `y` behind. The
      // echo has to be followed by whitespace, by punctuation, or by the end of the text.
      const match = rest.match(
        new RegExp(`^(\\s*)["'“‘]?${escapeForRegExp(echo)}["'”’]?(?=[\\s.,;:!?)\\]}]|$)`),
      );
      if (match) {
        cursor += match[0].length;
        break;
      }
    }
  }

  return result + text.slice(cursor);
};
